import React, { useState, useEffect, useRef, useContext } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import ReactRouterPropTypes from "react-router-prop-types";
import qs from "qs";
import { Alert } from "reactstrap";
import { debug } from "helpers";
import Loading from "components/Loading";
import { StoreContext } from "components/StoreContext";
import Filters from "./Filters";
import Results from "./Results";

const productsDefault = {
  filters: [],
  results: [],
  resultsTotal: 0,
  page: 1,
  pages: 1
};

const Products = props => {
  const {
    location,
    title,
    category,
    partner,
    keyword,
    ready,
    returnProductCount
  } = props;
  const { store, StoreAPI } = useContext(StoreContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState(productsDefault);
  const [isFiltersVisible, setFiltersVisibility] = useState(false);
  const prevCategoryRef = useRef();
  const prevPartnerRef = useRef();
  let query;
  let prevCategory = null;
  let prevPartner = null;

  const convertFilters = filters => {
    const arr = [];
    Object.keys(filters).forEach(key => {
      arr.push(`${key}~eq~${filters[key]}`);
    });
    return `${arr.join(",")}`;
  };

  const getProducts = () => {
    const params = qs.parse(window.location.search.substr(1));
    const sortMap = {
      featured: "",
      "price-lh": "price~asc,productName~asc",
      "price-hl": "price~desc,productName~asc",
      "name-az": "productName~asc",
      "name-za": "productName~desc"
    };
    const defaults = {
      isfacetlist: true,
      PageSize: 10,
      CategoryId: category,
      sort: "",
      refineby: {},
      PageNumber: 1
    };
    let initialLoad = false;
    const next = {
      CategoryId: category,
      refineby: {}
    };
    let mergedParams = {};
    let filters = {};

    setLoading(true);
    setError(null);
    setFiltersVisibility(false);

    if (params.category) {
      next.CategoryId = params.category;
    }
    if (partner) {
      filters = { ...filters, ...{ brand: partner } };
    }
    if (params.sort) {
      next.sort = sortMap[params.sort];
    }
    if (params.sort) {
      next.sort = sortMap[params.sort];
    }
    if (params.filter) {
      filters = { ...filters, ...params.filter };
    }
    if (params.page) {
      next.PageNumber = params.page;
    }
    if (params.page && params.page > 1 && products.results.length < 1) {
      next.PageNumber = 1;
      next.PageSize = defaults.PageSize * params.page;
      initialLoad = true;
    }
    if (category !== prevCategory || partner !== prevPartner) {
      setProducts(productsDefault);
    }

    next.refineby = convertFilters(filters);

    mergedParams = { ...defaults, ...next };

    // Hannah Neal: If there is a search term (keyword), send a POST request, otherwise, send a GET request.
    if (keyword) {
      query = {
        method: "post",
        url: "products/search",
        params: mergedParams,
        data: {
          Keyword: keyword
        }
      };
    } else {
      query = {
        url: "products",
        params: mergedParams
      };
    }
    StoreAPI.request(query)
      .then(response => {
        setLoading(false);
        setError(null);
        setProducts({
          filters: response.data.facets,
          categories: response.data.categories,
          results:
            mergedParams.PageNumber > 1
              ? products.results.concat(response.data.products)
              : response.data.products,
          resultsTotal: response.data.totalResults,
          page: initialLoad ? Number(params.page) : response.data.pageNumber,
          pages: initialLoad
            ? Math.ceil(response.data.totalResults / defaults.PageSize)
            : response.data.totalPages
        });
        if (document.activeElement) {
          document.activeElement.blur();
        }
        returnProductCount(response.data.totalResults);
      })
      .catch(() => {
        const message = `There was a problem getting the list of products.`;
        setLoading(false);
        setError(message);
        returnProductCount(0);
        debug("Products API Error Message:", message);
      });
  };

  useEffect(() => {
    if (store.loaded) {
      if (ready) {
        prevCategoryRef.current = category;
        prevPartnerRef.current = partner;
        getProducts();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    store.loaded,
    location.pathname,
    location.search,
    category,
    partner,
    ready
  ]);

  prevCategory = prevCategoryRef.current;
  prevPartner = prevPartnerRef.current;

  return (
    <div>
      {ready && (
        <section className="products">
          {title && (
            <section className="products__header">
              <h2 className="products__title">
                {`${title} Courses`}
                {products.results.length > 0 && (
                  <small>
                    {`(Showing ${products.results.length} of ${products.resultsTotal})`}
                  </small>
                )}
              </h2>
            </section>
          )}
          {error && <Alert color="danger">{error}</Alert>}
          <section className="products__body">
            {products.filters && products.filters.length > 0 && (
              <Filters
                loading={loading}
                filters={products.filters}
                isFiltersVisible={isFiltersVisible}
                setFiltersVisibility={setFiltersVisibility}
                partner={partner}
                categories={products.categories || []}
                category={category}
                keyword={keyword}
              />
            )}
            {products.results && products.results.length > 0 && (
              <Results
                loading={loading}
                results={products.results}
                setFiltersVisibility={setFiltersVisibility}
                page={products.page}
                pages={products.pages}
              />
            )}
            {loading && (
              <div className="products__loading">
                <Loading bg="rgba(244,244,244,.7)" />
              </div>
            )}
          </section>
        </section>
      )}
    </div>
  );
};

Products.propTypes = {
  location: ReactRouterPropTypes.location.isRequired,
  title: PropTypes.string,
  category: PropTypes.number,
  partner: PropTypes.string,
  keyword: PropTypes.string,
  ready: PropTypes.bool,
  returnProductCount: PropTypes.func
};

Products.defaultProps = {
  title: "All",
  category: 0,
  partner: null,
  ready: true,
  keyword: null,
  returnProductCount: () => {}
};

export default withRouter(Products);
